import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Camera } from 'lucide-react';
import './App.css';

import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import Navigation from './components/Navigation';
import NoticeTicker from './components/NoticeTicker'; 

// Route-based code splitting: Lazy loaded components to reduce initial bundle size
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const ReportsExport = lazy(() => import('./components/ReportsExport'));
const PunchSystem = lazy(() => import('./components/PunchSystem'));
const LeaveManagement = lazy(() => import('./components/LeaveManagement'));
const ProfileSetup = lazy(() => import('./components/ProfileSetup'));
const AnnouncementsAdmin = lazy(() => import('./components/AnnouncementsAdmin'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Eagerly set authorization header if token exists
const savedToken = localStorage.getItem('token');
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

const RouteLoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[50vh] w-full">
    <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  // Initialize user immediately from localStorage to eliminate waterfall mount cascade
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error('Error parsing stored user:', e);
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogin = async (credentials) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Login failed';
      if (typeof detail === 'string') errorMsg = detail;
      else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (userData) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, userData);
      return { success: true, message: 'Registration successful! Please login.' };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Registration failed';
      if (typeof detail === 'string') errorMsg = detail;
      else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-200">
        <LoginForm 
          onLogin={handleLogin} 
          onRegister={handleRegister}
          loading={loading}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950 transition-colors duration-200 overflow-hidden relative">
      <Router>
        <div className="shrink-0 z-50 shadow-sm relative">
          <Navigation user={user} onLogout={handleLogout} />
          <NoticeTicker user={user} /> 
        </div>

        <div className="flex-1 overflow-y-auto w-full relative">
          <Suspense fallback={<RouteLoadingSpinner />}>
            <Routes>
              <Route path="/" element={
                user?.role === 'admin' ? 
                <AdminDashboard /> : 
                <Dashboard user={user} />
              } />
              
              <Route path="/punch" element={<PunchSystem user={user} />} />
              <Route path="/profile" element={<ProfileSetup user={user} />} />
              <Route path="/leaves" element={<LeaveManagement user={user} />} />
              
              {user?.role === 'admin' && (
                <Route path="/announcements" element={<AnnouncementsAdmin />} />
              )}

              {['admin', 'hr'].includes(user?.role) && (
                <>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/users" element={<UserManagement user={user} />} />
                  <Route path="/reports" element={<ReportsExport user={user} />} />
                </>
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>

        {user?.role !== 'admin' && (
          <a href="/punch" className="md:hidden fixed bottom-6 right-6 flex items-center justify-center w-16 h-16 bg-gradient-to-tr from-blue-600 to-cyan-400 rounded-full shadow-[0_8px_30px_rgba(34,211,238,0.5)] text-white hover:scale-105 active:scale-95 transition-all z-[9999] border border-white/20">
            <Camera className="w-7 h-7" />
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-cyan-500 border-2 border-[#0B101E]"></span>
            </span>
          </a>
        )}
      </Router>
    </div>
  );
}

export default App;